import React, { useState, useEffect, useMemo } from "react";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import Student from "./Student";
import Tests from "./Marking/Tests";
import CourseList from "./Courses/CourseList";
import ReportsList from "./Reports/reportsList";
import CourseSelector from "./CourseSelector";
import CampusList from "./Campus/CampusList";
import UsersList from "./Users/UsersList";
import TopicsList from "./Topics/TopicsList";
import TeachersList from "./Teachers/TeachersList";
import Login from "./Login";
import Dashboard from "./Dashboard";
import { FirebaseDataProvider, useFirebaseData } from "./contexts/FirebaseDataContext";
import { Box, CircularProgress, Typography } from "@mui/material";

// Global loader component that shows until Firebase data is loaded
const GlobalLoader = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundColor: '#f5f5f5',
      gap: 3
    }}
  >
    <Box
      component="img"
      src="/img_1.png"
      alt="Logo"
      sx={{
        width: 80,
        height: 80,
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        mb: 2
      }}
    />
    <CircularProgress size={50} sx={{ color: '#4CAF50' }} />
    <Typography variant="h6" sx={{ color: '#333', fontWeight: 600 }}>
      Student Management System
    </Typography>
    <Typography variant="body2" sx={{ color: '#666' }}>
      Loading data, please wait...
    </Typography>
  </Box>
);

// Wrapper component that shows loader until data is ready
const AppContent = ({ children, onNavClick, handleLogout, activeView }) => {
  const { loading: contextLoading, initialLoadComplete } = useFirebaseData();

  if (contextLoading || !initialLoadComplete) {
    return <GlobalLoader />;
  }

  return (
    <div className="App">
      <Navbar onNavClick={onNavClick} handleLogout={handleLogout} activeView={activeView} />
      <div style={{ margin: "4rem" }}>
        {children}
      </div>
    </div>
  );
};

function AppInner() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const pathToViewKey = useMemo(() => ({
    "/": "dashboard",
    "/attendance": "home",
    "/students": "students",
    "/tests": "test",
    "/subjects": "subject",
    "/campus": "campus",
    "/users": "users",
    "/reports": "report",
    "/topics": "topics",
    "/teachers": "teachers",
  }), []);

  const viewKeyToPath = useMemo(() => ({
    dashboard: "/",
    home: "/attendance",
    students: "/students",
    test: "/tests",
    subject: "/subjects",
    campus: "/campus",
    users: "/users",
    report: "/reports",
    topics: "/topics",
    teachers: "/teachers",
  }), []);

  const currentViewKey = pathToViewKey[location.pathname] || "home";

  // Get user type from localStorage
  const getUserType = () => {
    try {
      return localStorage.getItem('userType') || 'User';
    } catch (error) {
      return 'User';
    }
  };

  const userType = getUserType();

  // Check if current route is restricted for president
  const isRestrictedRoute = useMemo(() => {
    if (userType !== 'President') return false;
    const restrictedPaths = ['/tests', '/subjects', '/campus', '/users', '/attendance', '/students', '/teachers'];
    return restrictedPaths.includes(location.pathname);
  }, [location.pathname, userType]);

  // Redirect president away from restricted routes
  useEffect(() => {
    if (isRestrictedRoute) {
      navigate("/", { replace: true });
    }
  }, [isRestrictedRoute, navigate]);

  const handleNavClick = (view) => {
    const targetPath = viewKeyToPath[view] || "/";
    if (location.pathname !== targetPath) {
      navigate(targetPath);
    }
  };

  const handleLogout = async () => {
    const auth = getAuth();
    // Clear all localStorage data
    localStorage.clear();
    await signOut(auth);
    setUser(null);
    navigate("/login", { replace: true });
  };

  if (loading) {
    return (
      <div className="App" style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #4CAF50',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            animation: 'spin 1s linear infinite',
            margin: '0 auto'
          }}></div>
          <p style={{ marginTop: '20px', color: '#666', fontSize: '16px' }}>Loading...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="App">
        <Routes>
          <Route
            path="/login"
            element={<Login onLoginSuccess={() => navigate("/", { replace: true })} />}
          />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>
    );
  }

  return (
    <FirebaseDataProvider>
      <AppContent 
        onNavClick={handleNavClick} 
        handleLogout={handleLogout} 
        activeView={currentViewKey}
      >
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/attendance" element={<CourseSelector />} />
          <Route path="/students" element={<Student />} />
          <Route path="/tests" element={<Tests />} />
          <Route path="/subjects" element={<CourseList />} />
          <Route path="/campus" element={<CampusList />} />
          <Route path="/users" element={<UsersList />} />
          <Route path="/reports" element={<ReportsList />} />
          <Route path="/topics" element={<TopicsList />} />
          <Route path="/teachers" element={<TeachersList />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppContent>
    </FirebaseDataProvider>
  );
}

function App() {
  return <AppInner />;
}

export default App;
